import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Dropdown, Table, Form, InputGroup, Modal, Button } from 'react-bootstrap';
import { FaDownload, FaSearch } from "react-icons/fa";
import { collection, getDocs, query, orderBy, getCountFromServer, limit, startAfter, where, startAt, endAt } from 'firebase/firestore';
import { db } from '../firebase';
import Filter from './Filter';
import NextPrevPagination from './NextPrevPagination';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const TrTable = () => {
  const [transactions, setTransactions] = useState([]);
  const [order, setOrder] = useState('desc');
  const [localSearch, setLocalSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [pageDocs, setPageDocs] = useState([null]);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 20;
  const [selectedOption, setSelectedOption] = useState("Download");
  const [errorDetailsTxn, setErrorDetailsTxn] = useState(null);

  // Debounce search input to avoid excessive Firestore reads
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(localSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [localSearch]);

  const fetchTotalCount = async () => {
    try {
      const qVal = searchQuery.trim();
      if (!qVal) {
        const q = query(collection(db, 'transactions'));
        const snapshot = await getCountFromServer(q);
        setTotalCount(snapshot.data().count);
      } else {
        const queries = [];
        // Query A: txnId prefix match with exact search query
        queries.push(
          getCountFromServer(
            query(collection(db, 'transactions'), orderBy('txnId'), startAt(qVal), endAt(qVal + '\uf8ff'))
          )
        );

        // Query B: txnId prefix match with uppercase search query (if different)
        const upperVal = qVal.toUpperCase();
        if (upperVal !== qVal) {
          queries.push(
            getCountFromServer(
              query(collection(db, 'transactions'), orderBy('txnId'), startAt(upperVal), endAt(upperVal + '\uf8ff'))
            )
          );
        }

        // Query C: phone prefix match (only if the search query doesn't contain letters)
        if (!/[a-zA-Z]/.test(qVal)) {
          queries.push(
            getCountFromServer(
              query(collection(db, 'transactions'), orderBy('phone'), startAt(qVal), endAt(qVal + '\uf8ff'))
            )
          );
        }

        const snapshots = await Promise.all(queries);
        const total = snapshots.reduce((sum, snap) => sum + snap.data().count, 0);
        setTotalCount(total);
      }
    } catch (error) {
      console.error('Error fetching transaction count:', error);
      setTotalCount(0);
    }
  };

  const fetchPage = async (pageNumber, startDoc) => {
    try {
      setLoading(true);
      const qVal = searchQuery.trim();

      if (!qVal) {
        const constraints = [orderBy('createdAt', order)];
        if (startDoc) {
          constraints.push(startAfter(startDoc));
        }
        constraints.push(limit(itemsPerPage));

        const q = query(collection(db, 'transactions'), ...constraints);
        const querySnapshot = await getDocs(q);
        const data = querySnapshot.docs.map(doc => doc.data());
        setTransactions(data);

        const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
        setPageDocs(prev => {
          const next = [...prev];
          next[pageNumber] = lastDoc;
          return next;
        });
      } else {
        const lastTxnDoc = startDoc?.lastTxnDoc || null;
        const lastTxnUpperDoc = startDoc?.lastTxnUpperDoc || null;
        const lastPhoneDoc = startDoc?.lastPhoneDoc || null;

        const queries = [];
        const queryKeys = [];

        // Query 1: txnId exact prefix
        const q1Constraints = [orderBy('txnId'), startAt(qVal), endAt(qVal + '\uf8ff')];
        if (lastTxnDoc) q1Constraints.push(startAfter(lastTxnDoc));
        q1Constraints.push(limit(itemsPerPage));
        queries.push(getDocs(query(collection(db, 'transactions'), ...q1Constraints)));
        queryKeys.push('txnId');

        // Query 2: txnId uppercase prefix (if different)
        const upperVal = qVal.toUpperCase();
        if (upperVal !== qVal) {
          const q2Constraints = [orderBy('txnId'), startAt(upperVal), endAt(upperVal + '\uf8ff')];
          if (lastTxnUpperDoc) q2Constraints.push(startAfter(lastTxnUpperDoc));
          q2Constraints.push(limit(itemsPerPage));
          queries.push(getDocs(query(collection(db, 'transactions'), ...q2Constraints)));
          queryKeys.push('txnIdUpper');
        }

        // Query 3: phone prefix (if no letters)
        if (!/[a-zA-Z]/.test(qVal)) {
          const q3Constraints = [orderBy('phone'), startAt(qVal), endAt(qVal + '\uf8ff')];
          if (lastPhoneDoc) q3Constraints.push(startAfter(lastPhoneDoc));
          q3Constraints.push(limit(itemsPerPage));
          queries.push(getDocs(query(collection(db, 'transactions'), ...q3Constraints)));
          queryKeys.push('phone');
        }

        const snapshots = await Promise.all(queries);

        const mergedMap = new Map();
        let newLastTxnDoc = null;
        let newLastTxnUpperDoc = null;
        let newLastPhoneDoc = null;

        snapshots.forEach((snap, idx) => {
          const key = queryKeys[idx];
          const lastDocOfSnap = snap.docs[snap.docs.length - 1] || null;
          if (key === 'txnId') newLastTxnDoc = lastDocOfSnap;
          if (key === 'txnIdUpper') newLastTxnUpperDoc = lastDocOfSnap;
          if (key === 'phone') newLastPhoneDoc = lastDocOfSnap;

          snap.docs.forEach(docSnap => {
            const item = docSnap.data();
            const uniqueKey = docSnap.id || item.txnId || item.id;
            mergedMap.set(uniqueKey, item);
          });
        });

        const mergedDocs = Array.from(mergedMap.values());
        mergedDocs.sort((a, b) => {
          const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
          const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
          return order === 'desc' ? timeB - timeA : timeA - timeB;
        });

        const finalDocs = mergedDocs.slice(0, itemsPerPage);
        setTransactions(finalDocs);

        setPageDocs(prev => {
          const next = [...prev];
          next[pageNumber] = {
            lastTxnDoc: newLastTxnDoc,
            lastTxnUpperDoc: newLastTxnUpperDoc,
            lastPhoneDoc: newLastPhoneDoc
          };
          return next;
        });
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initFetch = async () => {
      setPageDocs([null]);
      setCurrentPage(1);
      await fetchTotalCount();
      await fetchPage(1, null);
    };

    initFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, searchQuery]);

  const handleFilterChange = (e) => {
    setOrder(e.target.value);
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      fetchPage(prevPage, pageDocs[prevPage - 1]);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchPage(nextPage, pageDocs[nextPage - 1]);
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;

  const handleSelect = (option) => {
    setSelectedOption(option);
    generateExcel(option);
  };

  const generateExcel = async (period) => {
    const now = new Date();
    const oneDayAgo = new Date(now);
    oneDayAgo.setDate(now.getDate() - 1);
    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(now.getDate() - 7);
    const oneMonthAgo = new Date(now);
    oneMonthAgo.setMonth(now.getMonth() - 1);

    let filteredTransactions = [];

    try {
      setLoading(true);
      let q;
      if (period === "Daily") {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        q = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'), where('createdAt', '>=', startOfToday));
      } else if (period === "Weekly") {
        q = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'), where('createdAt', '>=', oneWeekAgo));
      } else if (period === "Monthly") {
        q = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'), where('createdAt', '>=', oneMonthAgo));
      } else {
        q = query(collection(db, 'transactions'), orderBy('createdAt', 'desc'));
      }

      const querySnapshot = await getDocs(q);
      filteredTransactions = querySnapshot.docs.map(doc => doc.data());
    } catch (error) {
      console.error('Error fetching transactions for Excel:', error);
      alert('Failed to fetch transactions for Excel report generation.');
      return;
    } finally {
      setLoading(false);
    }

    const groupKeyFunc = (item) => {
      const date = item.createdAt?.toDate?.() || new Date();

      if (period === "Daily" || period === "Weekly") {
        return date.toISOString().slice(0, 10); // yyyy-mm-dd
      } else if (period === "Monthly") {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // yyyy-mm
      }
    };

    const grouped = {};
    filteredTransactions.forEach((item) => {
      const key = groupKeyFunc(item);
      if (!grouped[key]) {
        grouped[key] = { count: 0, totals: {} };
      }
      grouped[key].count += 1;
      const currency = item.currencyCode || item.currency || 'USD';
      grouped[key].totals[currency] = (grouped[key].totals[currency] || 0) + (item.amountSender || 0);
    });

    const worksheetData = [["Period", "Total Transactions", "Total Amount"]];
    for (const [key, value] of Object.entries(grouped)) {
      const totalsStr = Object.entries(value.totals)
        .map(([curr, amt]) => `${curr} ${amt.toFixed(2)}`)
        .join(', ');
      worksheetData.push([key, value.count, totalsStr]);
    }

    const overallTotalTransactions = filteredTransactions.length;
    const overallTotals = {};
    filteredTransactions.forEach((item) => {
      const currency = item.currencyCode || item.currency || 'USD';
      overallTotals[currency] = (overallTotals[currency] || 0) + (item.amountSender || 0);
    });
    const overallTotalsStr = Object.entries(overallTotals)
      .map(([curr, amt]) => `${curr} ${amt.toFixed(2)}`)
      .join(', ');

    worksheetData.push([]);
    worksheetData.push(["Overall Total", overallTotalTransactions, overallTotalsStr]);

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "MReloadpay Summary");

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(data, `Transactions_${period}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const isFailedOrRefunded = (status) => {
    const s = status?.toLowerCase();
    return s === 'failed' || s === 'refunded' || s === 'unsuccessful';
  };

  const handleStatusClick = (item) => {
    if (isFailedOrRefunded(item.status)) {
      setErrorDetailsTxn(item);
    }
  };

  const renderErrorModal = () => {
    if (!errorDetailsTxn) return null;
    
    const response = errorDetailsTxn.response;
    const errorMsg = response?.error;
    const errorDetails = response?.errorDetails;

    const detailsList = [];
    if (Array.isArray(errorDetails)) {
      errorDetails.forEach(ed => {
        if (Array.isArray(ed.details)) {
          ed.details.forEach(d => {
            if (d.errorCode || d.message) {
              detailsList.push({
                code: d.errorCode || 'N/A',
                message: d.message || 'No message provided'
              });
            }
          });
        } else if (ed.details && (ed.details.errorCode || ed.details.message)) {
          detailsList.push({
            code: ed.details.errorCode || 'N/A',
            message: ed.details.message || 'No message provided'
          });
        } else if (ed.errorCode || ed.message) {
          detailsList.push({
            code: ed.errorCode || 'N/A',
            message: ed.message || 'No message provided'
          });
        }
      });
    } else if (errorDetails && typeof errorDetails === 'object') {
      if (Array.isArray(errorDetails.details)) {
        errorDetails.details.forEach(d => {
          if (d.errorCode || d.message) {
            detailsList.push({
              code: d.errorCode || 'N/A',
              message: d.message || 'No message provided'
            });
          }
        });
      } else if (errorDetails.errorCode || errorDetails.message) {
        detailsList.push({
          code: errorDetails.errorCode || 'N/A',
          message: errorDetails.message || 'No message provided'
        });
      }
    }

    return (
      <Modal show={!!errorDetailsTxn} onHide={() => setErrorDetailsTxn(null)} centered size="lg">
        <Modal.Header closeButton className="theme-modal-header">
          <Modal.Title style={{ color: 'var(--accent-lime)' }}>Transaction Error Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="theme-modal-body">
          <div className="mb-3">
            <span className="fw-semibold" style={{ color: 'var(--text-secondary)' }}>Transaction ID:</span>{' '}
            <span className="fw-bold" style={{ color: 'var(--text-primary)' }}>{errorDetailsTxn.txnId}</span>
          </div>
          <div className="mb-3">
            <span className="fw-semibold" style={{ color: 'var(--text-secondary)' }}>Status:</span>{' '}
            <span className="badge text-uppercase" style={{ backgroundColor: 'var(--danger)' }}>{errorDetailsTxn.status}</span>
          </div>

          <hr style={{ borderColor: 'var(--divider)' }} />

          {/* Main Error */}
          <div className="mb-3">
            <h5 style={{ color: 'var(--danger)' }} className="fw-semibold">Error Summary</h5>
            <div className="p-3 rounded" style={{ backgroundColor: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--danger)' }}>
              {errorMsg || 'No error message available.'}
            </div>
          </div>

          {/* Error Details */}
          {detailsList.length > 0 && (
            <div className="mb-3">
              <h5 className="fw-semibold" style={{ color: 'var(--accent-lime)' }}>Error Details</h5>
              <div className="table-responsive">
                <Table size="sm" className="mb-0" style={{ borderColor: 'var(--divider)' }}>
                  <thead>
                    <tr style={{ borderColor: 'var(--divider)' }}>
                      <th style={{ width: '30%', backgroundColor: 'var(--bg-primary)', color: 'var(--text-secondary)', borderColor: 'var(--divider)' }}>Error Code</th>
                      <th style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-secondary)', borderColor: 'var(--divider)' }}>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailsList.map((d, idx) => (
                      <tr key={idx} style={{ borderColor: 'var(--divider)' }}>
                        <td style={{ backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)', borderColor: 'var(--divider)' }}><code style={{ color: 'var(--accent-lime)' }}>{d.code}</code></td>
                        <td style={{ backgroundColor: 'var(--bg-surface)', color: 'var(--text-primary)', borderColor: 'var(--divider)' }}>{d.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {/* Raw JSON */}
          <div className="mt-4">
            <h5 className="fw-semibold" style={{ color: 'var(--text-secondary)' }}>Raw Response Details</h5>
            <pre
              className="p-3 rounded theme-modal-body"
              style={{
                maxHeight: '200px',
                overflow: 'auto',
                backgroundColor: 'var(--bg-primary)',
                border: '1px solid var(--divider)',
                fontSize: '0.85rem',
                color: 'var(--text-secondary)'
              }}
            >
              {JSON.stringify(response || null, null, 2)}
            </pre>
          </div>
        </Modal.Body>
        <Modal.Footer className="theme-modal-footer">
          <Button variant="secondary" onClick={() => setErrorDetailsTxn(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <section className="px-1 py-4 nav-body-bg btm-rad">
      <Container>
        <Row className="align-items-center justify-content-between flex-column flex-md-row text-center text-md-start">
          <Col xs="auto" className="mb-3 mb-md-0">
            <h3 className="text-h2">All Transactions</h3>
          </Col>

          <Col md={4} className="mb-3 mb-md-0">
            <InputGroup className="theme-search-group">
              <Form.Control
                type="text"
                placeholder="Search txnId or phone number"
                className="db-set-style border-0 shadow-none"
                style={{ backgroundColor: 'transparent' }}
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
              />
              <InputGroup.Text className="bg-transparent border-0">
                <FaSearch style={{ color: 'var(--accent-lime)' }} />
              </InputGroup.Text>
            </InputGroup>
          </Col>

          <Col xs="auto" className="d-flex align-items-center gap-2">
            <Filter value={order} onChange={handleFilterChange} />
            <Dropdown>
              <Dropdown.Toggle
                id="dropdown-download"
                className="theme-dropdown-toggle"
              >
                <FaDownload className="me-2" />
                {selectedOption}
              </Dropdown.Toggle>

              <Dropdown.Menu className="theme-dropdown-menu">
                <Dropdown.Item onClick={() => handleSelect("Daily")}>Daily</Dropdown.Item>
                <Dropdown.Item onClick={() => handleSelect("Weekly")}>Weekly</Dropdown.Item>
                <Dropdown.Item onClick={() => handleSelect("Monthly")}>Monthly</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>

        <div className="table-responsive scrollable-table-y mt-4" style={{ minHeight: '100vh', overflowY: 'auto' }}>
          <style>{`
            @media (max-width: 768px) {
              .scrollable-table-y {
                max-height: 300px;
                overflow-y: auto;
              }
            }
          `}</style>
          <Table className="table-hover align-middle body-text-two mb-0">
            <thead className="sticky-top top-0" style={{ zIndex: 10, background: 'transparent !important' }}>
              <tr>
                <th className='transparent-header'>Transaction ID</th>
                <th className="text-center transparent-header">Time & Date</th>
                <th className="text-center transparent-header">Recipient Phone</th>
                <th className="text-center transparent-header">Operator</th>
                <th className="text-center transparent-header">Amount</th>
                <th className="text-center transparent-header">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center transparent-header py-4">
                    <div className="admin-loader"></div>
                  </td>
                </tr>
              ) : transactions.length > 0 ? (
                transactions.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--divider)' }}>
                    <td className="py-3 transparent-header">{item.txnId}</td>
                    <td className="py-3 transparent-header text-center">
                      {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleString() : 'N/A'}
                    </td>
                    <td className="py-3 transparent-header text-center">{item.phone}</td>
                    <td className="py-3 transparent-header text-center">{item.operator?.name || 'N/A'}</td>
                    <td className="py-3 transparent-header text-center">
                     {item.amountSender?.toFixed(2) || '0.00'} {item.currencyCode || item.currency || 'USD'} 
                    </td>
                    <td 
                      className={`py-3 transparent-header text-center ${item.status === 'success' ? 'text-success' : 'text-danger'}`}
                      style={{
                        cursor: isFailedOrRefunded(item.status) ? 'pointer' : 'default',
                        textDecoration: isFailedOrRefunded(item.status) ? 'underline' : 'none'
                      }}
                      onClick={() => handleStatusClick(item)}
                      title={isFailedOrRefunded(item.status) ? "Click to view error details" : undefined}
                    >
                      {item.status}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center transparent-header py-4">No transactions found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <NextPrevPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={handlePrev}
          onNext={handleNext}
        />
        {renderErrorModal()}
      </Container>
    </section>
  );
};

export default TrTable;
